import React from 'react';
import { useTranslation } from 'react-i18next';
import { translateBlockValue } from 'i18n';
import {
    VOTED_FIELD,
    REALIZED_FIELD,
    GESTIONNAIRE_FIELD,
    REPORTED_FIELD,
    PHASE_AMOUNT,
} from 'routes/BudgetModule/config/constants';
import {
    getColnameValueByYear,
    getFieldByExercice,
} from 'routes/BudgetModule/helpers/format';
import {
    sumMultipleMetricByDim,
    groupByDim,
    getAggregatedData,
} from 'routes/BudgetModule/helpers/dataOperation';
import {
    BudgetProposalT,
    BudgetProposalSettingsT,
    StatusesSettingsT,
} from 'routes/BudgetModule/config/types';
import AutosizerFlex from 'components/AutosizerFlex';
import { camelize, snakify } from 'helpers/syntaxHelper';
import { TRACKABLE_EVENTS, trackEventBudget } from 'helpers/trackerHelpers';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import MultiplePaneChart from './MultiplePaneChart';
import { StyHeader, StyTitle, StyVerticalLayout } from './styles';
import BudgetAutocomplete from 'routes/BudgetModule/components/shared/BudgetAutocomplete/BudgetAutocomplete';
import { ExpenseIncomeImputationDataT } from '../../hooks/expenseIncomeImputations';
import { useIntegrationSoftwares } from 'helpers/integrations';
import { getDisplayedImputationsOptions } from '../helper';
import { useGetPhases } from 'routes/BudgetModule/shared/phases/hook';
import {
    getMetricByYear,
    getPhaseSettings,
} from 'routes/BudgetModule/shared/phases/phase';

type Props = {
    budgetProposal: BudgetProposalT;
    settings: BudgetProposalSettingsT | undefined;
    onSettingsEdit: (arg1: Partial<BudgetProposalSettingsT>) => void;
    statusesSettings: StatusesSettingsT;
    filteredImputationData: ExpenseIncomeImputationDataT[];
    dataByPhase: (InscriptionT | ResponseDataT)[];
    isReportedAmountUsed: boolean;
};

const BudgetProposalChart = ({
    budgetProposal,
    settings,
    onSettingsEdit,
    statusesSettings,
    filteredImputationData,
    dataByPhase,
    isReportedAmountUsed,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const { data: integratedSoftwares } = useIntegrationSoftwares();
    const { phase, exercice } = budgetProposal;
    const { data: phases = [] } = useGetPhases();
    const phaseSettings = getPhaseSettings(phases, phase);

    const votedMetric = VOTED_FIELD;
    const realizedMetric = REALIZED_FIELD;
    const imputationDimension = settings
        ? camelize(settings.imputationDimension)
        : GESTIONNAIRE_FIELD;

    const displayedImputationsOptions = getDisplayedImputationsOptions(
        Array.from(integratedSoftwares ?? []),
    );

    /**
    Setting for the multiple panel chart. This chart has two panes:
    - one showing the percentage of money 'realized' last year
    - the other showing -- for the dimension selected by the year --
         1. the amount voted last year versus
         2. a metric that depend on the phase
    Get metric names to show in the following charts
     */

    /**
     Metric that depends on the phase in the inscription data.
    */
    const metricByPhaseByYear = getMetricByYear(phaseSettings, exercice);
    /**
    Metrics in the imputations data pivoted by year.
    */
    const votedLastYear = getColnameValueByYear(votedMetric, exercice - 1);
    const realizedLastYear = getColnameValueByYear(
        realizedMetric,
        exercice - 1,
    );
    const realizedByYear = getColnameValueByYear(realizedMetric, exercice - 1);

    const paneSettings = {
        yAxisName: t('budgetProposal.yAxisChartBySelectedDimBottomPane'),
        metrics: [votedLastYear, realizedLastYear, metricByPhaseByYear],
    };

    /**
    Imputation metrics needs to grouped by the selected dimension
    and sum up across the values this dimension
    */
    const imputationGrouped = sumMultipleMetricByDim(
        filteredImputationData,
        [
            votedLastYear,
            PHASE_AMOUNT,
            getFieldByExercice(REPORTED_FIELD, exercice),
        ],
        imputationDimension,
        statusesSettings,
    );

    const realizedGroupedData = getAggregatedData(
        filteredImputationData,
        imputationDimension,
        realizedByYear,
        realizedLastYear,
    );

    /**
    When the budget proposal phase is 'arbitrage', simulated amount are edited
    at the level of an inscription line.
    Simulated amount needs to be sum across inscriptions line by the selected dim
     */
    const inscriptionsGrouped = sumMultipleMetricByDim(
        dataByPhase,
        [PHASE_AMOUNT],
        imputationDimension,
        statusesSettings,
    );
    const inscriptionsGroupedWithAmountLabel = inscriptionsGrouped.map(
        (inscription) => ({
            ...inscription,
            [metricByPhaseByYear]: inscription[PHASE_AMOUNT],
        }),
    );
    /**
    We concatenated all the computed data that we will be display in the chart
    */
    const multiPanesData = [
        ...imputationGrouped,
        ...inscriptionsGroupedWithAmountLabel,
        ...realizedGroupedData,
    ];

    /**
    we need to group these concatenated data by the selected dimension
    */
    let groupedBySelectedDim = groupByDim(multiPanesData, imputationDimension);
    if (isReportedAmountUsed) {
        groupedBySelectedDim = groupedBySelectedDim.map(
            (bp: Record<string, number>) => {
                return {
                    ...bp,
                    [metricByPhaseByYear]:
                        bp[metricByPhaseByYear] +
                        bp[getFieldByExercice(REPORTED_FIELD, exercice)],
                };
            },
        );
    }

    return (
        <StyVerticalLayout size={2}>
            <StyHeader>
                <StyTitle variant="body1">
                    {t('budgetProposal.chartBySelectedDim')}
                </StyTitle>
                <BudgetAutocomplete
                    disableClearable
                    options={displayedImputationsOptions}
                    getOptionLabel={(option) => translateBlockValue(option)}
                    className="budget-arbitrage-input"
                    value={imputationDimension}
                    onChange={(option: string) => {
                        onSettingsEdit({
                            ...settings,
                            imputationDimension: snakify(option),
                        });
                        trackEventBudget(
                            TRACKABLE_EVENTS.budget.budgetProposal.expenseIncome
                                .chartBySelectedDim,
                            { option: option },
                        );
                    }}
                    inputWidth="13rem"
                />
            </StyHeader>
            <AutosizerFlex>
                {({ height, width }) => (
                    <MultiplePaneChart
                        data={groupedBySelectedDim}
                        paneSettings={paneSettings}
                        selectedDimension={imputationDimension}
                        height={height}
                        width={width}
                        exercice={exercice}
                    />
                )}
            </AutosizerFlex>
        </StyVerticalLayout>
    );
};

export default BudgetProposalChart;
